import React from "react";
import { ArrowUpRight } from "lucide-react";
import { localized, useLanguage } from "@/i18n";

// Built / completed work at Pinnegar Hayward Design
export const BUILT_PROJECTS = [
  {
    title: "Replacement Fire Doorsets",
    year: "2022 – 2024",
    location: "QEH · Birmingham",
    description: "Over 2,000 fire doorsets surveyed, designed and replaced across the Queen Elizabeth Hospital. A Dynamo-driven BIM workflow was used to coordinate type, hardware and life-safety specifications at scale.",
    image: "https://images.unsplash.com/photo-1580582932707-520aed937b7b?auto=format&fit=crop&w=1200&q=80",
    annotation: "live hospital",
    details: ["Type — Healthcare, life-safety", "Scope — 2,000+ doorsets", "Tools — Revit · Dynamo · BIM", "Practice — Pinnegar Hayward Design"],
  },
  {
    title: "Park & Ride Facilities",
    year: "2023",
    location: "The Asps · Leamington Spa",
    description: "A new park-and-ride hub designed to ease town-centre congestion. The pavilion houses waiting areas, accessible WCs and information points beneath a generous canopy that frames arrival.",
    image: "https://images.unsplash.com/photo-1518684079-3c830dcef090?auto=format&fit=crop&w=1200&q=80",
    annotation: "arrival canopy",
    details: ["Type — Public infrastructure", "Brief — Sustainable transport", "Stage — Built", "Practice — Pinnegar Hayward Design"],
  },
  {
    title: "New Occupational Health Suite",
    year: "2023",
    location: "Mellor House · Stafford",
    description: "A new occupational-health suite carved into Mellor House, with consultation rooms, therapy spaces and reception clustered around a calm circulation spine.",
    image: "https://images.unsplash.com/photo-1631815589968-fdb09a223b1e?auto=format&fit=crop&w=1200&q=80",
    annotation: "calm interior",
    details: ["Type — Healthcare interior", "Stage — Completed", "Sector — NHS", "Practice — Pinnegar Hayward Design"],
  },
  {
    title: "Same Day Emergency Care Unit",
    year: "2024",
    location: "QEH · Birmingham",
    description: "A new SDEC unit adding 16 beds and two single ensuite rooms to expand rapid assessment capacity. Designed to compress patient pathways while improving privacy and dignity.",
    image: "https://images.unsplash.com/photo-1538108149393-fbbd81895907?auto=format&fit=crop&w=1200&q=80",
    annotation: "16 new beds",
    details: ["Type — Acute healthcare", "Capacity — 16 beds + 2 ensuite", "Stage — Delivered", "Practice — Pinnegar Hayward Design"],
  },
  {
    title: "Children's & Women's Hospital WCs",
    year: "2023",
    location: "Birmingham",
    description: "Refurbishment of patient and visitor toilets across two NHS sites, including new Changing Places facilities. A child-friendly palette and accessibility-first detailing throughout.",
    image: "https://images.unsplash.com/photo-1631549916768-4119b2e5f926?auto=format&fit=crop&w=1200&q=80",
    annotation: "changing places",
    details: ["Type — Healthcare refurbishment", "Sites — Steelhouse Ln. + Mindelsohn Way", "Standards — Doc M / Changing Places", "Practice — Pinnegar Hayward Design"],
  },
  {
    title: "PHD Studio Office",
    year: "2023",
    location: "87 Carver Street · Birmingham",
    description: "Practice's own studio fit-out — meeting rooms, library, kitchen and a 'gallery of work' that doubles as informal review pin-up space. Custom shelving and bi-fold partitions.",
    image: "https://images.unsplash.com/photo-1497366754035-f200968a6e72?auto=format&fit=crop&w=1200&q=80",
    annotation: "gallery wall",
    details: ["Type — Workplace fit-out", "Brief — Studio HQ", "Stage — Built", "Practice — Pinnegar Hayward Design"],
  },
  {
    title: "Chemotherapy Day Unit",
    year: "Ongoing",
    location: "Alexandra Hospital, Redditch",
    description: "Feasibility study exploring relocation and expansion options for the chemotherapy day unit — balancing patient comfort, dignity and staff workflow.",
    image: "https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?auto=format&fit=crop&w=1200&q=80",
    annotation: "feasibility",
    details: ["Type — Healthcare", "Status — Feasibility", "Progress — 45%", "Next — Client decision on Option 5", "Practice — Pinnegar Hayward Design"],
  },
  {
    title: "Refurbishment of Angio 1",
    year: "Ongoing",
    location: "Queen Elizabeth Hospital, Birmingham",
    description: "Upgrading the existing Angio 1 unit to receive new clinical equipment, with new ceilings, finishes, and clinical workstations coordinated around live theatre operations.",
    image: "https://images.unsplash.com/photo-1504439468489-c8920d796a29?auto=format&fit=crop&w=1200&q=80",
    annotation: "tech design",
    details: ["Type — Healthcare refurbishment", "Status — Technical design", "Progress — 70%", "Next — Detail package issue", "Practice — Pinnegar Hayward Design"],
  },
  {
    title: "Relocation of HM & CAMHS",
    year: "Ongoing",
    location: "Town Hall, Redditch",
    description: "Adapting a heritage town hall to house mental-health and CAMHS services. Careful balance of clinical compliance, accessibility and the building's civic character.",
    image: "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=1200&q=80",
    annotation: "construction",
    details: ["Type — Healthcare adaptation", "Status — Construction", "Progress — 82%", "Next — Site coordination", "Practice — Pinnegar Hayward Design"],
  },
  {
    title: "Master of Architecture · Part 2",
    year: "2023 – present",
    location: "Birmingham City University",
    description: "Ongoing Part 2 studies — researching computational design, conceptual frameworks, and how architecture can engage with futures, automation and ecologies.",
    image: "https://images.unsplash.com/photo-1503387762-592deb58ef4e?auto=format&fit=crop&w=1200&q=80",
    annotation: "riba part 2",
    details: ["Type — Academic research", "Status — RIBA Part 2", "Progress — 60%", "Next — Thesis development"],
  },
];

const projectId = (project) => project.id || `built-${project.title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`;

export const BuiltProjectsContent = ({ onOpen, customProjects = [], deletedProjectIds = [] }) => (
  <BuiltProjectsList onOpen={onOpen} customProjects={customProjects} deletedProjectIds={deletedProjectIds} />
);

const BuiltProjectsList = ({ onOpen, customProjects, deletedProjectIds }) => {
  const { lang, t } = useLanguage();
  const deleted = new Set(deletedProjectIds);
  const customIds = new Set(customProjects.map(projectId));
  const items = [
    ...customProjects,
    ...BUILT_PROJECTS.filter((project) => !customIds.has(projectId(project))),
  ].filter((project) => !deleted.has(projectId(project)));

  return (
  <div className="space-y-6">
    {items.map((p, i) => {
      const project = {
        ...p,
        title: localized(p, "title", lang),
        location: localized(p, "location", lang),
        description: localized(p, "description", lang),
        annotation: localized(p, "annotation", lang),
      };

      return (
      <article
        key={projectId(p)}
        data-testid={`built-item-${i}`}
        className="group border border-dashed border-[var(--paper-edge)] p-3 hover:border-ink/40 transition"
      >
        <div className="relative overflow-hidden">
          {p.image ? (
            <img src={p.image} alt={project.title} className="w-full h-44 object-cover transition duration-700 group-hover:scale-[1.02]" />
          ) : (
            <div className="w-full h-44 bg-[var(--bg-board-2)] grid place-items-center text-ink-faint font-mono text-[10px] uppercase tracking-[0.18em]">Draft</div>
          )}
          <span className="absolute top-2 left-2 font-mono text-[10px] tracking-[0.2em] uppercase bg-paper/90 text-ink px-2 py-1">
            {t("no")} {String(i + 1).padStart(3, "0")}
          </span>
        </div>
        <div className="pt-3">
          <h3 className="font-display font-medium text-ink leading-tight">{project.title}</h3>
          <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-ink-faint mt-1">
            {p.year} · {project.location}
          </p>
          <p className="text-sm text-ink-soft mt-2 leading-relaxed">{project.description}</p>
        </div>
        <button
          data-testid={`btn-view-built-${i}`}
          onClick={() => onOpen({ ...project, kind: t("built"), category: project.location || project.category, no: i + 1 })}
          className="mt-3 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-ink hover:text-accentRed transition"
        >
          {t("viewProject")} <ArrowUpRight size={13} />
        </button>
      </article>
      );
    })}
  </div>
  );
};
